import { action, computed, observable } from 'mobx'
import { QuestionAnswerEditorViewModelBase } from '@/features/jobs/questions/QuestionAnswerEditorViewModelBase'
import {
  type DateTimeQuestion,
  type DateTimeAnswer,
} from '@/features/jobs/questions/questions'
import { QuestionValidationResult } from '@/features/jobs/questions/question-validation-result'
import { deepEqual } from '@/utils/object-util'

export class DateTimeQuestionAnswerEditorViewModel extends QuestionAnswerEditorViewModelBase<DateTimeQuestion> {
  /** @inheritDoc */
  readonly type = 'DateTime'

  @observable accessor value: Date | null = null
  @observable accessor collectTime = false
  @observable accessor disallowPast = false
  @observable accessor disallowFuture = false

  constructor(
    private readonly _getAsOfDate: () => Date,
    private readonly _now: () => Date,
  ) {
    super()
  }

  /** @inheritDoc */
  @computed get isAnswered(): boolean {
    return this.value !== null
  }

  @computed get minDate() {
    if (this.disallowPast) {
      return this._getAsOfDate()
    }

    return null
  }

  @computed get maxDate() {
    if (this.disallowFuture) {
      return new Date()
    }

    return null
  }

  /** @inheritDoc */
  @computed get answer(): DateTimeAnswer | null {
    if (!this.value) {
      return null
    }

    return {
      type: 'DateTime',
      value: this.value,
    }
  }

  /** @inheritDoc */
  @computed get textAnswer() {
    if (this.value === null) {
      return null
    }

    return this.value.toLocaleDateString()
  }

  /** @inheritDoc */
  @action.bound
  respond(value: Date | null) {
    if (deepEqual(this.value, value)) {
      return
    }

    if (value) {
      if (this.minDate && value < this.minDate) {
        value = this.minDate
      }

      if (this.maxDate && value > this.maxDate) {
        value = this.maxDate
      }
    }

    this.value = value
    this.notifyAnswerChanged()
  }

  /** @inheritDoc */
  set(question: DateTimeQuestion): void {
    this.collectTime = question.collectTime
    this.disallowPast = question.disallowPast
    this.disallowFuture = question.disallowFuture
    this.value = question.answer ? question.answer.value : null
  }

  /** @inheritDoc */
  validate(): QuestionValidationResult {
    if (!this.answer) {
      return QuestionValidationResult.failed(['Please enter a date'])
    }

    return QuestionValidationResult.ok()
  }
}
